import React from 'react'
import {
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Divider,
    Icon,
    Tooltip,
    Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { calculateOrderExecutionTiming, formatExecutionDuration } from 'app/utils/orderExecutionTiming'

const useStyles = makeStyles(() => ({
    chip: {
        height: 23,
        borderRadius: 7,
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
        '& .MuiChip-label': {
            paddingLeft: 8,
            paddingRight: 8,
        },
    },
    overdue: {
        color: '#b91c1c',
        background: '#fef2f2',
        border: '1px solid #fecaca',
    },
    warning: {
        color: '#92400e',
        background: '#fffbeb',
        border: '1px solid #fde68a',
    },
    dialogPaper: {
        borderRadius: 12,
        background: '#ffffff',
    },
    dialogTitle: {
        padding: '14px 18px 10px',
        borderBottom: '1px solid #e5e7eb',
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
    },
    section: {
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        padding: 12,
        marginBottom: 12,
        background: '#ffffff',
    },
    sectionHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8,
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 18,
        padding: '4px 0',
        fontSize: 13,
    },
    key: {
        color: '#64748b',
    },
    value: {
        color: '#1f2937',
        fontWeight: 600,
        textAlign: 'right',
    },
    reason: {
        marginTop: 9,
        padding: '8px 10px',
        borderRadius: 8,
        fontSize: 13,
        lineHeight: 1.45,
    },
    reasonBad: {
        color: '#991b1b',
        background: '#fef2f2',
        border: '1px solid #fecaca',
    },
    reasonGood: {
        color: '#166534',
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
    },
    tooltip: {
        fontSize: 12,
        lineHeight: 1.45,
        maxWidth: 300,
    },
}))

const formatDateTime = (timestamp) => {
    const value = Number(timestamp || 0)
    return value > 0 ? new Date(value * 1000).toLocaleString('ru-RU') : '—'
}

const DetailRow = ({ label, value, classes }) => (
    <div className={classes.row}>
        <span className={classes.key}>{label}</span>
        <span className={classes.value}>{value}</span>
    </div>
)

const OrderExecutionIndicator = ({ order = {}, statuses = [], nowMs = Date.now(), style = {} }) => {
    const classes = useStyles()
    const [open, setOpen] = React.useState(false)
    const timing = calculateOrderExecutionTiming(order, statuses, nowMs)
    const overallWarning = timing.configured && timing.remainingSeconds <= 15 * 60
    const finalWarning = timing.finalPlannedMinutes > 0 && timing.finalRemainingSeconds <= 15 * 60
    const stageWarning = timing.stageLimitMinutes > 0 && timing.stageRemainingSeconds <= 15 * 60
    const isOverdue = timing.overdue || timing.finalOverdue || timing.stageOverdue || timing.historicalOverdue

    if (timing.isTerminal || (!overallWarning && !finalWarning && !stageWarning && !timing.historicalOverdue)) return null

    const orderNumber = Number(order?.numberOrder || order?.number_order || order?.id || 0)
    const tooltipLines = []
    if (timing.historicalOverdue) {
        tooltipLines.push(`Ранее нарушено нормативов: ${timing.historicalViolations.length}`)
    }
    if (timing.configured) {
        tooltipLines.push(timing.overdue
            ? `Текущая контрольная точка просрочена на ${formatExecutionDuration(timing.overdueSeconds)}`
            : `До контрольной точки ${formatExecutionDuration(timing.remainingSeconds)}`)
    }
    if (timing.finalPlannedMinutes > 0) {
        tooltipLines.push(timing.finalOverdue
            ? `Итоговый срок просрочен на ${formatExecutionDuration(timing.finalOverdueSeconds)}`
            : `До выполнения заказа ${formatExecutionDuration(timing.finalRemainingSeconds)}`)
    }
    if (timing.stageLimitMinutes > 0) {
        tooltipLines.push(timing.stageOverdue
            ? `Этап «${timing.statusName || 'Текущий'}» просрочен на ${formatExecutionDuration(timing.stageOverdueSeconds)}`
            : `До лимита этапа ${formatExecutionDuration(timing.stageRemainingSeconds)}`)
    }

    const openDetails = (event) => {
        event.preventDefault()
        event.stopPropagation()
        setOpen(true)
    }
    const closeDetails = (event) => {
        if (event?.stopPropagation) event.stopPropagation()
        setOpen(false)
    }

    return (
        <>
            <Tooltip
                arrow
                title={(
                    <div className={classes.tooltip}>
                        {tooltipLines.map((line) => <div key={line}>{line}</div>)}
                        <div style={{ marginTop: 4, opacity: 0.8 }}>Нажмите, чтобы посмотреть расчёт</div>
                    </div>
                )}
            >
                <Chip
                    size="small"
                    label={isOverdue ? 'Просрочен' : 'До срока'}
                    onClick={openDetails}
                    className={`${classes.chip} ${isOverdue ? classes.overdue : classes.warning}`}
                    style={style}
                />
            </Tooltip>
            <Dialog
                open={open}
                onClose={closeDetails}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: classes.dialogPaper }}
                onClick={(event) => event.stopPropagation()}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <div className={classes.titleRow}>
                        <Icon style={{ color: isOverdue ? '#dc2626' : '#d97706' }}>
                            {isOverdue ? 'schedule' : 'timer'}
                        </Icon>
                        <div>
                            <Typography variant="h6">Срок исполнения заказа{orderNumber > 0 ? ` №${orderNumber}` : ''}</Typography>
                            <Typography variant="caption" style={{ color: '#64748b' }}>
                                Текущий статус: {timing.statusName || 'не указан'}
                            </Typography>
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent style={{ padding: 16 }}>
                    {timing.historicalOverdue ? (
                        <div className={classes.section} style={{ borderColor: '#fecaca', background: '#fffafa' }}>
                            <div className={classes.sectionHead}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#991b1b' }}>Ранее нарушенные нормативы</Typography>
                                <Chip size="small" label={timing.historicalViolations.length} className={classes.overdue} />
                            </div>
                            {timing.historicalViolations.map((violation, index) => (
                                <div key={`${violation.type}_${violation.statusId}_${index}`} style={{ marginTop: index > 0 ? 10 : 0 }}>
                                    {index > 0 ? <Divider style={{ marginBottom: 8 }} /> : null}
                                    <DetailRow
                                        classes={classes}
                                        label={violation.type === 'stage' ? `Этап «${violation.statusName}»` : `Контрольная точка «${violation.statusName}»`}
                                        value={`просрочено на ${formatExecutionDuration(violation.overdueSeconds)}`}
                                    />
                                    <DetailRow
                                        classes={classes}
                                        label="Норматив / фактически"
                                        value={`${formatExecutionDuration(Number(violation.limitMinutes || 0) * 60)} / ${formatExecutionDuration(violation.actualSeconds)}`}
                                    />
                                    <DetailRow classes={classes} label="Нарушение зафиксировано" value={formatDateTime(violation.violatedAt)} />
                                </div>
                            ))}
                            <div className={`${classes.reason} ${classes.reasonBad}`}>
                                Смена статуса не отменяет уже зафиксированное нарушение. Оно остаётся в истории заказа.
                            </div>
                        </div>
                    ) : null}

                    <div className={classes.section}>
                        <div className={classes.sectionHead}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Итоговый срок заказа</Typography>
                            <Chip size="small" label={timing.finalPlannedMinutes > 0 ? (timing.finalOverdue ? 'Просрочен' : 'В срок') : 'Не настроен'} className={timing.finalOverdue ? classes.overdue : undefined} />
                        </div>
                        {timing.finalPlannedMinutes > 0 ? (
                            <>
                                <DetailRow classes={classes} label="Целевой статус" value={timing.finalStatusName} />
                                <DetailRow classes={classes} label="Итоговый норматив" value={formatExecutionDuration(timing.finalPlannedMinutes * 60)} />
                                <DetailRow classes={classes} label="Заказ создан" value={formatDateTime(timing.createdAt)} />
                                <DetailRow classes={classes} label="Выполнить до" value={formatDateTime(timing.finalDeadlineAt)} />
                                <Divider style={{ margin: '6px 0' }} />
                                <DetailRow
                                    classes={classes}
                                    label={timing.finalOverdue ? 'Итоговая просрочка' : 'Осталось до выполнения'}
                                    value={formatExecutionDuration(timing.finalOverdue ? timing.finalOverdueSeconds : timing.finalRemainingSeconds)}
                                />
                                <div className={`${classes.reason} ${timing.finalOverdue ? classes.reasonBad : classes.reasonGood}`}>
                                    {timing.finalOverdue
                                        ? `Заказ не переведён в «${timing.finalStatusName}» за ${formatExecutionDuration(timing.finalPlannedMinutes * 60)} с момента создания.`
                                        : `Итоговый срок действует во всех рабочих статусах, даже если у текущего статуса свой норматив не задан.`}
                                </div>
                            </>
                        ) : (
                            <Typography variant="body2" style={{ color: '#64748b' }}>У статуса «Выполнен» итоговый норматив не задан.</Typography>
                        )}
                    </div>

                    <div className={classes.section}>
                        <div className={classes.sectionHead}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Контрольная точка текущего статуса</Typography>
                            <Chip size="small" label={timing.configured ? (timing.overdue ? 'Просрочен' : 'В срок') : 'Не настроен'} className={timing.overdue ? classes.overdue : undefined} />
                        </div>
                        {timing.configured ? (
                            <>
                                <DetailRow classes={classes} label="Заказ создан" value={formatDateTime(timing.createdAt)} />
                                <DetailRow classes={classes} label="Норматив текущего статуса" value={formatExecutionDuration(timing.plannedMinutes * 60)} />
                                <DetailRow classes={classes} label="Прошло с создания" value={formatExecutionDuration(timing.orderElapsedSeconds)} />
                                <DetailRow classes={classes} label="Расчётный срок" value={formatDateTime(timing.deadlineAt)} />
                                <Divider style={{ margin: '6px 0' }} />
                                <DetailRow
                                    classes={classes}
                                    label={timing.overdue ? 'Просрочка' : 'Осталось'}
                                    value={formatExecutionDuration(timing.overdue ? timing.overdueSeconds : timing.remainingSeconds)}
                                />
                                <div className={`${classes.reason} ${timing.overdue ? classes.reasonBad : classes.reasonGood}`}>
                                    {timing.overdue
                                        ? `С момента создания прошло ${formatExecutionDuration(timing.orderElapsedSeconds)}, что больше контрольного норматива ${formatExecutionDuration(timing.plannedMinutes * 60)}.`
                                        : `Контрольная точка ещё не просрочена. До неё осталось ${formatExecutionDuration(timing.remainingSeconds)}.`}
                                </div>
                            </>
                        ) : (
                            <Typography variant="body2" style={{ color: '#64748b' }}>Для текущего статуса общий норматив не задан.</Typography>
                        )}
                    </div>

                    <div className={classes.section} style={{ marginBottom: 0 }}>
                        <div className={classes.sectionHead}>
                            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Время текущего этапа</Typography>
                            <Chip size="small" label={timing.stageLimitMinutes > 0 ? (timing.stageOverdue ? 'Просрочен' : 'В срок') : 'Не настроен'} className={timing.stageOverdue ? classes.overdue : undefined} />
                        </div>
                        {timing.stageLimitMinutes > 0 ? (
                            <>
                                <DetailRow classes={classes} label="Этап" value={timing.statusName || 'Текущий статус'} />
                                <DetailRow classes={classes} label="Лимит этапа" value={formatExecutionDuration(timing.stageLimitMinutes * 60)} />
                                <DetailRow classes={classes} label="Ранее потрачено на этом этапе" value={formatExecutionDuration(timing.stageAccumulatedSecondsBeforeCurrent)} />
                                <DetailRow classes={classes} label="Текущий вход в этап" value={formatExecutionDuration(timing.stageCurrentEpisodeSeconds)} />
                                <DetailRow classes={classes} label="Всего потрачено на этапе" value={formatExecutionDuration(timing.stageTotalElapsedSeconds)} />
                                <Divider style={{ margin: '6px 0' }} />
                                <DetailRow
                                    classes={classes}
                                    label={timing.stageOverdue ? 'Просрочка этапа' : 'Осталось на этапе'}
                                    value={formatExecutionDuration(timing.stageOverdue ? timing.stageOverdueSeconds : timing.stageRemainingSeconds)}
                                />
                                <div className={`${classes.reason} ${timing.stageOverdue ? classes.reasonBad : classes.reasonGood}`}>
                                    {timing.stageOverdue
                                        ? `Во всех входах в статус «${timing.statusName || 'Текущий'}» накоплено ${formatExecutionDuration(timing.stageTotalElapsedSeconds)} при лимите ${formatExecutionDuration(timing.stageLimitMinutes * 60)}.`
                                        : `Этап укладывается в лимит. Повторные входы и смены исполнителя не обнуляют уже потраченное время.`}
                                </div>
                            </>
                        ) : (
                            <Typography variant="body2" style={{ color: '#64748b' }}>Для текущего статуса отдельный лимит этапа не задан.</Typography>
                        )}
                    </div>
                </DialogContent>
                <DialogActions style={{ borderTop: '1px solid #e5e7eb', padding: '10px 16px' }}>
                    <Button onClick={closeDetails} color="primary" variant="contained">Закрыть</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default OrderExecutionIndicator
